import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CardModule } from '@openng/optimus-ui/card';
import { TableModule } from '@openng/optimus-ui/table';
import { TagModule } from '@openng/optimus-ui/tag';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { ButtonModule } from '@openng/optimus-ui/button';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { PricingService } from '../../services/pricing.service';
import { UiStateService } from '../../services/ui-state.service';
import { OrgPricingAnalysis } from '../../models/pricing.types';
import { PeriodOption } from '../../models/dashboard.types';
import { DateRangeSelectorComponent, DateRange } from '../shared/date-range-selector.component';
import { ErrorBannerComponent } from '../shared/error-banner.component';

@Component({
  selector: 'app-pricing-plans',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    TableModule,
    TagModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    DateRangeSelectorComponent,
    ErrorBannerComponent,
  ],
  templateUrl: './pricing-plans.component.html',
  styleUrl: './pricing-plans.component.scss',
})
export class PricingPlansComponent {
  protected pricingService = inject(PricingService);
  protected uiState = inject(UiStateService);
  private router = inject(Router);

  protected selectedPeriod = this.uiState.selectedPeriod;
  protected startDate = this.uiState.startDate;
  protected endDate = this.uiState.endDate;

  protected showDetailSidebar = signal(false);
  protected search = signal('');
  protected detailOrgId = signal<number | null>(null);

  protected filteredUsage = computed(() => {
    const term = this.search().trim().toLowerCase();
    const all = this.pricingService.orgUsageAnalysis();
    if (!term) return all;
    return all.filter(
      (o) => o.orgName.toLowerCase().includes(term) || o.planName.toLowerCase().includes(term),
    );
  });

  constructor() {
    effect(() => {
      this.uiState.startDate();
      this.uiState.endDate();
      untracked(() => this.loadData());
    });
  }

  loadData() {
    const { startDate, endDate } = this.uiState.getRange();
    this.pricingService.loadPricingAnalysis(startDate, endDate);
  }

  onPeriodChange(period: PeriodOption) {
    this.uiState.setPeriod(period);
  }

  onDateRangeChange(dateRange: DateRange) {
    this.uiState.setRange(dateRange.startDate, dateRange.endDate);
  }

  openDetail(item: OrgPricingAnalysis) {
    const { startDate, endDate } = this.uiState.getRange();
    this.detailOrgId.set(item.orgId);
    this.pricingService.loadPricingDetail(item.orgId, startDate, endDate);
    this.showDetailSidebar.set(true);
  }

  openOrgForDetail() {
    const orgId = this.detailOrgId();
    if (orgId != null) {
      this.router.navigate(['/organizations', orgId]);
    }
  }

  openOrg(orgId: number) {
    this.router.navigate(['/organizations', orgId]);
  }

  openUserSessions(userId: number) {
    this.uiState.setSessionUserFilter(userId);
    this.router.navigate(['/sessions']);
  }

  getUtilizationPercent(used: number, cap: number): number {
    if (cap === 0) return used > 0 ? 100 : 0;
    return Math.min(100, (used / cap) * 100);
  }

  getUtilizationClass(used: number, cap: number): string {
    const pct = this.getUtilizationPercent(used, cap);
    if (pct >= 90) return 'danger';
    if (pct >= 75) return 'warn';
    return 'success';
  }
}
